import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "@mui/material";
import { useFormikContext } from "formik";
import { useLocation, useParams } from "react-router";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  Spinner,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import { useGetBlackOutDays } from "../../../queries/transactionV2";
import { formatDisplayDate } from "../../../utils/date";
import { useGetDesignatedPersons } from "../../../queries/designatedPerson";
import { TransactionDetailsData } from "../../../types/Transaction";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import {
  saveTransactionDetails,
  updateBlackOutDayInMonetaryTransaction,
} from "../../../api/TransactionApisV2";

type SelectBlackoutStepProps = {
  onStepChange: (e?: number) => void;
  onBackClick: (e?: number) => void;
};

export const SelectBlackoutStep = ({
  onBackClick,
  onStepChange,
}: SelectBlackoutStepProps) => {
  const formik = useFormikContext<TransactionDetailsData>();
  const [search, setSearch] = useState("");
  const location = useLocation();
  const params = useParams();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;
  const [selectedBlackouts, setSelectedBlackouts] = useState<string[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const { data: blackoutDays, isLoading } = useGetBlackOutDays();

  useEffect(() => {
    if (formik.values.monetaryDetails?.blackOutDays?.length) {
      setSelectedBlackouts(formik.values.monetaryDetails.blackOutDays);
    }
  }, [formik.values.monetaryDetails?.blackOutDays]);

  const filteredBlackouts = useMemo(() => {
    if (!blackoutDays) return [];
    return blackoutDays.filter((b: any) => {
      const reason = b.reason?.toLowerCase() || "";
      const dpName = b.designatedPerson?.name?.toLowerCase() || "";
      const q = search.toLowerCase();
      return reason.includes(q) || dpName.includes(q);
    });
  }, [blackoutDays, search]);

  const handleSelect = (id: string) => {
    setSelectedBlackouts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleViewDetails = (data: any) => {
    setModalData(data);
    setModalOpen(true);
  };

  const handleNext = () => {
    formik.setFieldValue("monetaryDetails.blackOutDays", selectedBlackouts);
    if (!id) {
      saveTransactionDetails(formik.values);
    }
    onStepChange();
  };

  const [blackoutsChanged, setBlackoutsChanged] = useState(false);

  useEffect(() => {
    setBlackoutsChanged(
      !_.isEqual(
        selectedBlackouts,
        formik.values.monetaryDetails.blackOutDays || []
      )
    );
  }, [selectedBlackouts, formik.values.monetaryDetails.blackOutDays]);

  const handleSaveBlackouts = () => {
    formik.setFieldValue("monetaryDetails.blackOutDays", selectedBlackouts);
    if (!id) return;
    updateBlackOutDayInMonetaryTransaction({
      blackOutDays: selectedBlackouts,
      transactionId: id,
    })
      .then(() => {
        toast.success("Blackout days saved.");
        setBlackoutsChanged(false);
      })
      .catch((error: any) => {
        toast.error(`Failed to save blackout days: ${error.message}`);
      });
  };

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between text-xl font-semibold text-black-501">
          BlackOut Days
        </HStack>
        <HStack className="flex text-sm font-medium text-gray-500">
          Manage blackout periods for employees.
        </HStack>
      </VStack>
      {/* ...search input if needed... */}
      <Box className="w-full p-6 min-h-[200px]">
        {isLoading ? (
          <Spinner />
        ) : filteredBlackouts.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No applicable blackout periods found for the selected transaction
            dates.
          </div>
        ) : (
          <VStack className="gap-4">
            {filteredBlackouts.map((b: any) => (
              <BlackoutPeriodCard
                key={b.id}
                reason={b.reason}
                startDate={b.startDate}
                endDate={b.endDate}
                noOfPersons={b.designatedPerson?.employeesList.length || 0}
                isSelected={selectedBlackouts.includes(b.id)}
                onSelect={() => handleSelect(b.id)}
                onViewDetails={() => handleViewDetails(b)}
              />
            ))}
          </VStack>
        )}
      </Box>
      <Dialog
        open={modalOpen}
        maxWidth="md"
        onClose={() => setModalOpen(false)}
      >
        {modalData && (
          <BlackoutDetailsModal
            blackoutData={modalData}
            onClose={() => setModalOpen(false)}
          />
        )}
      </Dialog>
      <HStack className="justify-between w-full px-6 mt-8">
        <ButtonPrimary1 onClick={() => onBackClick()}>Back</ButtonPrimary1>
        <HStack className="gap-4">
          {mode === "edit" &&
            (formik.values.eventState === MonetaryEventState.STARTED ||
              formik.values.eventState === MonetaryEventState.CLOSED) && (
              <ButtonPrimary
                onClick={handleSaveBlackouts}
                disabled={!blackoutsChanged}
              >
                Save Blackout Days
              </ButtonPrimary>
            )}
          <ButtonPrimary
            onClick={handleNext}
            disabled={selectedBlackouts.length === 0}
          >
            Next
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export const BlackoutPeriodCard = ({
  reason,
  startDate,
  endDate,
  noOfPersons,
  isSelected,
  onSelect,
  onViewDetails,
}: {
  reason: string;
  startDate: string;
  endDate: string;
  noOfPersons: string;
  isSelected: boolean;
  onSelect: () => void;
  onViewDetails: () => void;
}) => (
  <Box
    className={`border rounded-lg p-4 flex items-center justify-between ${
      isSelected ? "border-orange-500 bg-orange-50" : "border-gray-200"
    }`}
  >
    <HStack className="items-center gap-4">
      <input type="checkbox" checked={isSelected} onChange={onSelect} />
      <VStack>
        <div className="font-semibold">{reason}</div>
        <div className="text-xs text-gray-500">
          {formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}
        </div>
        <div className="text-xs text-gray-400">List: {noOfPersons}</div>
      </VStack>
    </HStack>
    <ButtonPrimary1 onClick={onViewDetails}>View Details</ButtonPrimary1>
  </Box>
);

export const BlackoutDetailsModal = ({
  blackoutData,
  onClose,
}: {
  blackoutData: any;
  onClose: () => void;
}) => {
  const employees = blackoutData?.designatedPerson?.employeesList || [];
  return (
    <Box className="p-8 w-[600px] bg-white rounded-lg">
      <HStack className="justify-between mb-4">
        <h3 className="text-lg font-semibold">Blackout Details</h3>
        <span className="text-xl cursor-pointer" onClick={onClose}>
          ×
        </span>
      </HStack>
      <VStack className="gap-2 mb-4">
        <div>
          <b>Reason:</b> {blackoutData.reason}
        </div>
        <div>
          <b>Duration:</b> {formatDisplayDate(blackoutData.startDate)} -{" "}
          {formatDisplayDate(blackoutData.endDate)}
        </div>
        <div>
          <b>Notes:</b>{" "}
          {blackoutData.notes || (
            <span className="text-gray-400">No notes</span>
          )}
        </div>
        <div>
          <b>Designated Person List:</b> {blackoutData.designatedPerson?.name}
        </div>
      </VStack>
      <div className="mb-2 font-semibold">Employees</div>
      <Box className="p-2 overflow-y-auto border rounded max-h-48 bg-gray-50">
        {employees.length === 0 ? (
          <div className="text-sm text-gray-400">
            No employees in this list.
          </div>
        ) : (
          <ul className="text-sm">
            {employees.map((emp: any, idx: number) => (
              <li key={emp.employeeId || idx}>
                {emp.name}{" "}
                {emp.email && (
                  <span className="text-gray-400">({emp.email})</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Box>
      <HStack className="justify-end mt-6">
        <ButtonPrimary1 onClick={onClose}>Close</ButtonPrimary1>
      </HStack>
    </Box>
  );
};
